export interface Testimonial {
  id: string;
  category: 'older_adult' | 'family_member' | 'caregiver' | 'professional';
  name: string;
  role: string;
  location?: string;
  quote: string;
  title?: string;
  image?: string;
}

export const testimonials: Testimonial[] = [
  // Older Adults
  {
    id: 'oa1',
    category: 'older_adult',
    name: 'Margaret Thompson',
    role: 'Retired Teacher',
    location: 'Portland, OR',
    title: 'Living Confidently at 78',
    quote: 'SeniorThrive has given me the confidence to age in my own home. The safety assessments caught hazards I never noticed, and the daily check-ins keep me connected with my daughter. I feel empowered, not monitored.',
  },
  {
    id: 'oa2',
    category: 'older_adult',
    name: 'Robert Chen',
    role: 'Former Engineer',
    location: 'San Francisco, CA',
    title: 'Technology That Makes Sense',
    quote: 'As someone who worked in tech, I appreciate how intuitive SeniorThrive is. The health tracking helps me stay on top of my medications, and the community features keep me engaged with other seniors who share my interests.',
  },
  {
    id: 'oa3',
    category: 'older_adult',
    name: 'Dorothy Williams',
    role: 'Community Volunteer',
    location: 'Austin, TX',
    title: 'Staying Independent and Social',
    quote: 'The platform helps me maintain my independence while staying connected. I love sharing my daily activities with my family, and the safety features give everyone peace of mind. I\'m not just aging - I\'m thriving!',
  },

  // Family Members
  {
    id: 'fm1',
    category: 'family_member',
    name: 'Sarah Martinez',
    role: 'Daughter & Working Mother',
    location: 'Denver, CO',
    title: 'Peace of Mind Across Miles',
    quote: 'Living 500 miles away from Dad used to keep me up at night. Now I can check his wellness dashboard, coordinate with his caregivers, and even video chat during his daily activities. SeniorThrive brought our family closer.',
  },
  {
    id: 'fm2',
    category: 'family_member',
    name: 'Michael Johnson',
    role: 'Son & Healthcare Administrator',
    location: 'Chicago, IL',
    title: 'Coordinating Care Made Simple',
    quote: 'Managing Mom\'s care team was chaos before SeniorThrive. Now everyone - her home aide, physical therapist, and our family - stays coordinated through one platform. It\'s streamlined care coordination beyond my expectations.',
  },
  {
    id: 'fm3',
    category: 'family_member',
    name: 'Lisa Anderson',
    role: 'Daughter & Teacher',
    location: 'Seattle, WA',
    title: 'Supporting Mom\'s Independence',
    quote: "SeniorThrive helped us respect Mom's desire for independence while ensuring her safety. The smart insights catch potential issues early, and the family dashboard keeps everyone informed without being intrusive.",
  },

  // Caregivers
  {
    id: 'cg1',
    category: 'caregiver',
    name: 'Maria Rodriguez',
    role: 'Home Health Aide',
    location: 'Phoenix, AZ',
    title: 'Better Care Through Better Tools',
    quote: 'SeniorThrive has transformed how I provide care. I can track my clients\' progress, coordinate with families, and document everything efficiently. My clients feel more secure, and families see the quality of care I provide.',
  },
  {
    id: 'cg2',
    category: 'caregiver',
    name: 'Jennifer Davis',
    role: 'Certified Nursing Assistant',
    location: 'Atlanta, GA',
    title: 'Streamlined Documentation and Communication',
    quote: 'The platform makes documentation so much easier. I can update health metrics in real-time, share observations with the care team, and ensure nothing falls through the cracks. It\'s made me a more effective caregiver.',
  },
  {
    id: 'cg3',
    category: 'caregiver',
    name: 'Amanda Foster',
    role: 'Personal Care Assistant',
    location: 'Boston, MA',
    title: 'Empowering Caregivers with Technology',
    quote: 'I love how SeniorThrive recognizes the important work we do. The time tracking and progress reports help families understand our value, and the communication tools keep everyone connected. It\'s elevated my profession.',
  },

  // Professionals
  {
    id: 'p1',
    category: 'professional',
    name: 'Dr. Patricia Wilson',
    role: 'Geriatrician',
    location: 'Miami, FL',
    title: 'Proactive Healthcare Insights',
    quote: 'SeniorThrive provides invaluable data between appointments. I can see trends in my patients\' daily activities, medication adherence, and safety concerns. It\'s revolutionized how I deliver geriatric care.',
  },
  {
    id: 'p2',
    category: 'professional',
    name: 'Karen Thompson',
    role: 'Occupational Therapist',
    location: 'Nashville, TN',
    title: 'Data-Driven Therapy Planning',
    quote: 'The home safety assessments and activity tracking give me insights I never had before. I can tailor therapy plans based on real-world data and see how my interventions impact daily living. It\'s evidence-based OT at its best.',
  },
  {
    id: 'p3',
    category: 'professional',
    name: 'James Miller',
    role: 'Home Care Agency Owner',
    location: 'Orlando, FL',
    title: 'Growing Our Business with Better Outcomes',
    quote: 'SeniorThrive has helped us scale our agency while improving care quality. Families love the transparency, caregivers are more efficient, and we can demonstrate our value with data. It\'s been a game-changer for our business.',
  },
];

export const getCategoryTestimonials = (category: Testimonial['category']) => {
  return testimonials.filter(testimonial => testimonial.category === category);
};

export const getCategoryInfo = (category: Testimonial['category']) => {
  const categoryData = {
    older_adult: {
      title: 'Older Adults',
      subtitle: 'Stories of independence, confidence, and thriving at home',
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    family_member: {
      title: 'Family Members',
      subtitle: 'Stories of connection, coordination, and peace of mind',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    caregiver: {
      title: 'Caregivers',
      subtitle: 'Stories of better tools, efficiency, and meaningful care',
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
    professional: {
      title: 'Professionals',
      subtitle: 'Stories of better outcomes, insights, and business growth',
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
  };

  return categoryData[category];
};